import React, { useState, useRef, useEffect } from 'react';
import IntroVideoService from '../api/IntroVideoService';
import config from '../config';

const IntroVideoModal = ({ show, onVideoCompleted, organizationName }) => {
  const [videoUrl, setVideoUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [showCloseButton, setShowCloseButton] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (show && !videoUrl) {
      loadVideo();
    }
  }, [show]);

  const loadVideo = async () => {
    try {
      setLoading(true);
      setError('');
      // Use direct URL from config instead of requesting signed URL
      setVideoUrl(config.introVideo.url);
    } catch (err) {
      console.error('Error loading intro video:', err);
      setError('Error al cargar el video. Por favor, inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const current = videoRef.current.currentTime;
      const duration = videoRef.current.duration;
      const progressPercent = (current / duration) * 100;
      setProgress(progressPercent);

      // Show close button when video reaches 95% progress
      if (progressPercent >= 95 && !showCloseButton) {
        setShowCloseButton(true);
      }
    }
  };

  const handleVideoEnd = async () => {
    setShowCloseButton(true);
    await markAsWatched();
  };

  const markAsWatched = async () => {
    try {
      await IntroVideoService.markIntroVideoAsWatched(organizationName);
    } catch (err) {
      console.error('Error marking video as watched:', err);
    }
  };

  const handleClose = async () => {
    if (showCloseButton) {
      await markAsWatched();
      onVideoCompleted();
    }
  };

  if (!show) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 max-w-6xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* <h3 className="text-xl leading-6 font-medium text-gray-900 text-center mb-4">
            Introducción a Virtus Auto
          </h3> */}

          {loading && (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-gray-600">Cargando video...</span>
            </div>
          )}

          {error && (
            <div className="text-center p-4">
              <p className="text-red-600 mb-4">{error}</p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={loadVideo}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  Reintentar
                </button>
                <button
                  onClick={onVideoCompleted}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                >
                  Omitir Video
                </button>
              </div>
            </div>
          )}

          {videoUrl && !loading && (
            <div className="relative">
              <video
                ref={videoRef}
                src={videoUrl}
                className="w-full h-auto max-h-[60vh] rounded-lg"
                controls
                disablePictureInPicture
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleVideoEnd}
                onLoadedMetadata={() => setLoading(false)}
              />

              {/* Progress bar */}
              {/* <div className="mt-4 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600 mt-2 text-center">
                Progreso: {Math.round(progress)}%
                {progress < 95 &&
                  ' - Debes ver al menos el 95% del video para continuar'}
              </p> */}
            </div>
          )}

          {/* Close button - only shows after 95% progress */}
          {showCloseButton && (
            <div className="flex justify-center mt-6">
              <button
                onClick={handleClose}
                className="px-6 py-3 bg-green-500 text-white text-base font-medium rounded-md shadow-sm hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-300"
              >
                Continuar
              </button>
            </div>
          )}

          {!showCloseButton && (
            <div className="text-center mt-4">
              <p className="text-gray-600 text-sm">
                Visualice el video antes de continuar.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IntroVideoModal;
